<template>
  <v-dialog
    v-model="dialog"
    max-width="500px"
  >
    <v-card>
      <v-card-title>
        <span class="headline">Create rate</span>
      </v-card-title>
      <v-card-text>
        <v-container>
          <v-row>
            <v-col
              cols="12"
              sm="6"
            >
              <v-autocomplete
                v-model="rate.sendingCountryIso3"
                :items="listSourceCountries"
                label="Send country"
                item-value="codeIso3"
                :item-text="i => `${i.codeIso3}: ${i.name}`"
              />
            </v-col>
            <v-col
              cols="12"
              sm="6"
            >
              <v-autocomplete
                v-model="rate.sendingCurrency"
                :items="currencies"
                label="Sending Currency"
              />
            </v-col>
            <v-col
              cols="12"
              sm="6"
            >
              <v-autocomplete
                v-model="rate.receiveCountryIso3"
                :items="listDestCountries"
                item-value="codeIso3"
                :item-text="i => `${i.codeIso3}: ${i.name}`"
                label="Receive country"
              />
            </v-col>
            <v-col
              cols="12"
              sm="6"
            >
              <v-autocomplete
                v-model="rate.receiveCurrency"
                :items="currencies"
                label="Receiving Currency"
              />
            </v-col>
            <v-col
              cols="12"
              sm="6"
            >
              <v-autocomplete
                v-model="rate.product"
                :items="payoutMethods"
                label="Product"
                item-value="code"
                item-text="title"
              />
            </v-col>
            <v-col
              cols="12"
              sm="6"
            >
              <v-select
                v-model="rate.segment"
                :items="segmentOptions"
                item-text="text"
                item-value="value"
                label="Segment"
              />
            </v-col>
            <v-col
              cols="12"
              sm="6"
            >
              <v-text-field
                v-model="rate.marginDesired"
                label="Desired  rate"
                suffix="%"
                :rules="marginDesiredRules"
              />
            </v-col>
            <v-col
              v-if="rate.segment !== 'FIRST_TRANSACTION'"
              cols="12"
            >
              <v-switch
                v-model="marginRangesEnabled"
                inset
                dense
                label="Enable tiered mark-up"
                @change="onToggleMarginRanges"
              />
            </v-col>
            <v-col
              v-if="marginRangesEnabled"
              cols="12"
            >
              <div
                v-for="(tier, index) in rate.marginDesiredRanges"
                :key="`margin-tier-create-${index}`"
                class="margin-tier-card"
              >
                <v-btn
                  v-if="index > 0"
                  class="margin-tier-card__remove"
                  icon
                  plain
                  x-small
                  @click="onRemoveMarginRange(index)"
                >
                  <v-icon>mdi-close</v-icon>
                </v-btn>
                <v-row>
                  <v-col
                    cols="12"
                    sm="6"
                  >
                    <v-text-field
                      v-model.number="tier.minAmount"
                      type="number"
                      min="0.01"
                      label="Min amount"
                      :rules="tierMinAmountRules(tier, index)"
                    />
                  </v-col>
                  <v-col
                    cols="12"
                    sm="6"
                  >
                    <v-text-field
                      v-model.number="tier.marginPercent"
                      type="number"
                      min="0"
                      max="100"
                      label="Desired mark-up"
                      suffix="%"
                      :rules="tierMarginRules(tier)"
                    />
                  </v-col>
                </v-row>
              </div>
              <v-btn
                v-if="rate.marginDesiredRanges.length < marginRangesLimit"
                class="mt-2"
                color="primary"
                outlined
                block
                @click="onAddMarginRange"
              >
                <v-icon left>
                  mdi-plus
                </v-icon>
                Add mark-up tier
              </v-btn>
            </v-col>
          </v-row>
        </v-container>
      </v-card-text>

      <v-card-actions>
        <v-spacer />
        <v-btn @click="close">
          Cancel
        </v-btn>
        <v-btn
          color="primary"
          :loading="loading"
          :disabled="loading || !tierConfigurationValid"
          @click="save"
        >
          Create
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
// utils
import { rateCurrencies } from "@/utils/staticAutopricing";
import { toFloat } from '@/utils/helpers';

export default {
  name: "CreateRateModal",
  props: {
    value: {
      type: Boolean,
      default: false
    }
  },

  data: () => ({
    loading: false,
    rate: {},
    marginRangesEnabled: false,
    marginRangesLimit: 50,
    defaultRate: {
      sendingCountryIso3: "",
      sendingCurrency: "",
      receiveCountryIso3: "",
      receiveCurrency: "",
      product: "",
      desired_mark_up_from_spot_rate: "",
      minimum_markup_from_provider_rate: "",
      marketRate: "",
      providerRate: "",
      marginDesired: "",
      marginDesiredRanges: [],
      calcMinRate: "",
      calcEndUserRate: "",
      automaticallyRate: "",
      segment: "STANDARD"
    },
    currencies: rateCurrencies,
    segmentOptions: [
      { text: "Standard", value: "STANDARD" },
      { text: "First Transaction", value: "FIRST_TRANSACTION" }
    ]
  }),

  computed: {
    marginDesiredRules() {
      return this.marginRangesEnabled && this.rate.segment !== 'FIRST_TRANSACTION'
        ? [value => this.hasValue(value) || 'Base desired mark-up is required']
        : [];
    },
    tierConfigurationValid() {
      if (!this.marginRangesEnabled || this.rate.segment === 'FIRST_TRANSACTION') {
        return true;
      }
      if (!this.hasValue(this.rate.marginDesired)) {
        return false;
      }
      const ranges = this.rate.marginDesiredRanges || [];
      if (ranges.length === 0) {
        return false;
      }
      const amounts = ranges.map(tier => Number(tier?.minAmount));
      return ranges.every((tier, index) => {
        const minAmount = Number(tier?.minAmount);
        const marginPercent = Number(tier?.marginPercent);
        return this.hasValue(tier?.minAmount) && minAmount > 0 &&
          amounts.indexOf(minAmount) === index &&
          this.hasValue(tier?.marginPercent) && marginPercent >= 0 && marginPercent <= 100;
      });
    },
    listDestCountries() {
      return this.$store.getters["ActiveCountryStore/getListDestCountries"];
    },
    listSourceCountries() {
      return this.$store.getters["ActiveCountryStore/getListSourceCountries"];
    },
    payoutMethods() {
      return this.$store.getters["PayoutMethodStore/getList"];
    },
    dialog: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit("input", val);
      }
    }
  },

  watch: {
    dialog: {
      handler() {
        this.$set(this, "rate", JSON.parse(JSON.stringify(this.defaultRate)));
        this.marginRangesEnabled = false;
      }
    },
    "rate.segment"(segment) {
      if (segment === 'FIRST_TRANSACTION') {
        this.marginRangesEnabled = false;
        this.$set(this.rate, "marginDesiredRanges", []);
      }
    }
  },

  methods: {
    save() {
      if (!this.tierConfigurationValid) {
        return;
      }
      this.loading = true;
      const data = {
        marginDesired: toFloat(this.rate.marginDesired),
        marginDesiredRanges: this.buildMarginRanges(),
        sendingCountryIso3: this.rate.sendingCountryIso3,
        sendingCurrency: this.rate.sendingCurrency || null,
        receiveCountryIso3: this.rate.receiveCountryIso3,
        receiveCurrency: this.rate.receiveCurrency,
        serviceCode: this.rate.product,
        segment: this.rate.segment || "STANDARD"
      }
      this.$store
        .dispatch("RecordStore/create", data)
        .then(() => {
          this.$emit("save", this.rate);
          this.close();
        })
        .finally(() => {
          this.loading = false;
        });
    },
    onToggleMarginRanges(value) {
      if (value && this.rate.marginDesiredRanges.length === 0) {
        this.onAddMarginRange();
      }
    },
    onAddMarginRange() {
      if (!Array.isArray(this.rate.marginDesiredRanges)) {
        this.$set(this.rate, "marginDesiredRanges", []);
      }
      if (this.rate.marginDesiredRanges.length >= this.marginRangesLimit) {
        return;
      }
      this.rate.marginDesiredRanges.push({ minAmount: null, marginPercent: null });
    },
    onRemoveMarginRange(index) {
      this.rate.marginDesiredRanges.splice(index, 1);
    },
    buildMarginRanges() {
      if (!this.marginRangesEnabled || this.rate.segment === 'FIRST_TRANSACTION') {
        return [];
      }
      return (this.rate.marginDesiredRanges || [])
        .map(tier => ({
          minAmount: toFloat(tier.minAmount),
          marginPercent: toFloat(tier.marginPercent)
        }))
        .sort((a, b) => a.minAmount - b.minAmount);
    },
    hasValue(value) {
      return value !== null && value !== undefined && value !== '';
    },
    tierMinAmountRules(tier, index) {
      return [
        value => this.hasValue(value) || 'Min amount is required',
        value => Number(value) > 0 || 'Min amount must be greater than 0',
        value => (this.rate.marginDesiredRanges || []).findIndex(
          range => this.hasValue(range?.minAmount) && Number(range.minAmount) === Number(value)
        ) === index || 'Min amount must be unique'
      ];
    },
    tierMarginRules(tier) {
      return [
        () => this.hasValue(tier?.marginPercent) || 'Desired mark-up is required',
        () => {
          const value = Number(tier?.marginPercent);
          return value >= 0 && value <= 100 || 'Desired mark-up must be between 0 and 100';
        }
      ];
    },
    close() {
      this.dialog = false;
    }
  }
};
</script>

<style lang="scss" scoped>
.margin-tier-card {
  position: relative;
  margin-bottom: 1rem;
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 4px;
  padding: 8px 12px;

  &__remove {
    position: absolute;
    top: 4px;
    right: 4px;
    z-index: 2;
  }
}
</style>
